import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { authorizedAxios } from "@moonscape/shared";
import { useAuth0 } from "@auth0/auth0-react";
import { useWebSocketContext } from "@moonscape/shared";

// --- Types ---

export interface ImportOperation {
  id: string;
  label: string;
  state: "pending" | "in_progress" | "completed" | "failed" | "retrying";
  recordsProcessed: number;
  estimatedTotal: number | null;
  recordsPerSecond: number | null;
  durationMs: number | null;
  progress: number; // 0-100
}

export interface TaxStage {
  id: string;
  label: string;
  state: "pending" | "in_progress" | "completed" | "failed";
  durationMs?: number;
}

export interface ImportProgress {
  phase: "idle" | "validating" | "importing" | "calculating_taxes" | "completed" | "failed" | "stuck";
  operations: ImportOperation[];
  currentOperation: ImportOperation | null;
  taxStages: TaxStage[];
  currentTaxStage: TaxStage | null;
  overallProgress: number; // 0-100
  eta: number | null; // ms remaining
  totalRecords: number;
  startedAt: Date | null;
  error?: { message: string; retryable: boolean };
}

// --- API response shape from /api/sync/state/:exchangeId ---

export interface SyncStateResponse {
  phase: string;
  operations?: Array<{
    id: string;
    label: string;
    state: string;
    recordsProcessed?: number;
    estimatedTotal?: number;
    progressPercentage?: number;
  }>;
  importPhase?: {
    phase: string;
    operations: Array<{
      id: string;
      label: string;
      state: string;
    }>;
  };
  taxPhase?: {
    phase: string;
    stages: Array<{
      id: string;
      label: string;
      state: string;
      durationMs?: number;
    }>;
  } | null;
  overallProgress?: number;
  startedAt?: string | null;
  totalRecords?: number | null;
  error?: string;
}

// --- Constants ---

const STALE_TIMEOUT_MS = 30_000; // 30s no WS event → poll
const POLL_INTERVAL_MS = 5_000;

// --- Helpers ---

function mapPhase(raw: string): ImportProgress["phase"] {
  switch (raw) {
    case "validating":
    case "starting":
    case "queued":
      return "validating";
    case "importing":
    case "in_progress":
    case "processing":
      return "importing";
    case "calculating_taxes":
      return "calculating_taxes";
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    case "stuck":
      return "stuck";
    default:
      return "idle";
  }
}

function mapOperationState(raw: string): ImportOperation["state"] {
  switch (raw) {
    case "in_progress":
    case "processing":
      return "in_progress";
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    case "retrying":
      return "retrying";
    default:
      return "pending";
  }
}

function mapApiOperation(
  op: NonNullable<SyncStateResponse["operations"]>[number]
): ImportOperation {
  return {
    id: op.id,
    label: op.label,
    state: mapOperationState(op.state),
    recordsProcessed: op.recordsProcessed ?? 0,
    estimatedTotal: op.estimatedTotal ?? null,
    recordsPerSecond: null,
    durationMs: null,
    progress: op.progressPercentage ?? 0,
  };
}

function computeEta(current: ImportOperation | null): number | null {
  if (!current || !current.recordsPerSecond || current.recordsPerSecond <= 0) return null;
  if (!current.estimatedTotal) return null;
  const remaining = current.estimatedTotal - current.recordsProcessed;
  if (remaining <= 0) return 0;
  return (remaining / current.recordsPerSecond) * 1000;
}

/** One reading of a /sync/state snapshot, shared by the mount hydrate and the poll. */
export function deriveFromSnapshot(data: SyncStateResponse) {
  const phase = mapPhase(data.phase);
  const operations = (data.operations || []).map(mapApiOperation);
  const currentOperation = operations.find((o) => o.state === "in_progress") ?? null;

  // Tax runs after the import, so a taxPhase served while the import is still
  // going belongs to a previous run: the server reads the per-user tax row with
  // no recency check, and would otherwise paint last night's stages, durations
  // and all, onto a job that has not reached tax yet.
  const importRunning = phase === "importing" || phase === "validating";
  const taxStages: TaxStage[] = importRunning
    ? []
    : data.taxPhase?.stages?.map((stage) => ({
        id: stage.id,
        label: stage.label,
        state: stage.state as TaxStage["state"],
        durationMs: stage.durationMs,
      })) ?? [];
  const currentTaxStage = taxStages.find((s) => s.state === "in_progress") ?? null;

  // Import owns 0-70 of the bar, tax stages the last 30 — the same split the WS
  // handlers use, applied whether or not a tax phase came back.
  const importProgress = Math.min(data.overallProgress ?? 0, 100) * 0.7;
  const taxDone = taxStages.filter((s) => s.state === "completed").length;
  const overallProgress =
    phase === "completed" && taxStages.length === 0
      ? 100
      : Math.min(importProgress + (taxStages.length ? (taxDone / taxStages.length) * 30 : 0), 100);

  return {
    phase,
    operations,
    currentOperation,
    taxStages,
    currentTaxStage,
    overallProgress,
    eta: computeEta(currentOperation),
    totalRecords: data.totalRecords ?? 0,
    ...(data.error ? { error: { message: data.error, retryable: true } } : {}),
  };
}

const IDLE_STATE: ImportProgress = {
  phase: "idle",
  operations: [],
  currentOperation: null,
  taxStages: [],
  currentTaxStage: null,
  overallProgress: 0,
  eta: null,
  totalRecords: 0,
  startedAt: null,
};

// --- Hook ---

export const useImportProgress = (exchangeId: string | undefined): ImportProgress => {
  const { getAccessTokenSilently } = useAuth0();
  const { messages } = useWebSocketContext();

  const [progress, setProgress] = useState<ImportProgress>(IDLE_STATE);
  const lastWsEventAt = useRef<number>(0);
  const processedMsgKeys = useRef(new Set<string>());
  const operationStartTimes = useRef(new Map<string, number>());

  // --- 1. Initial snapshot fetch via React Query ---
  const { data: snapshot } = useQuery({
    queryKey: ["import-progress", exchangeId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const { data } = await authorizedAxios(token).get<SyncStateResponse>(
        `/sync/state/${exchangeId}`
      );
      return data;
    },
    enabled: !!exchangeId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    // Don't auto-refetch — we use WS + manual poll
    refetchInterval: false,
  });

  // Hydrate from snapshot on mount
  useEffect(() => {
    if (!snapshot) return;
    const phase = mapPhase(snapshot.phase);
    if (phase === "idle") {
      setProgress(IDLE_STATE);
      return;
    }

    setProgress({
      ...deriveFromSnapshot(snapshot),
      startedAt: snapshot.startedAt ? new Date(snapshot.startedAt) : null,
    });
  }, [snapshot]);

  // --- 2. WebSocket event processing ---
  useEffect(() => {
    if (!messages || messages.length === 0 || !exchangeId) return;

    messages.forEach((msg) => {
      // Only process sync-related and tax-related messages
      if (
        msg.type !== "sync" &&
        msg.type !== "sync_progress" &&
        msg.type !== "sync_state_change" &&
        msg.type !== "operation_progress" &&
        msg.type !== "tax_progress"
      )
        return;

      // Tax runs per user, not per exchange, so tax_progress carries no
      // exchangeId and must be handled before the exchange filter below —
      // otherwise every tax event is dropped for want of an id it never has.
      if (msg.type === "tax_progress") {
        lastWsEventAt.current = Date.now();
        setProgress((prev) => {
          const taxStages: TaxStage[] = msg.stages || prev.taxStages;
          const currentTaxStage =
            taxStages.find((s: TaxStage) => s.state === "in_progress") ?? null;

          if (msg.status === "failed") {
            return {
              ...prev,
              taxStages,
              currentTaxStage: null,
              error: {
                message: msg.error || "Tax calculation failed",
                retryable: true,
              },
            };
          }

          const completedStages = taxStages.filter(
            (s: TaxStage) => s.state === "completed"
          ).length;
          const totalStages = Math.max(taxStages.length, 1);
          // Tax calc occupies the last 30% of the bar; the import owns 0-70.
          const overallProgress = 70 + (completedStages / totalStages) * 100 * 0.3;

          return {
            ...prev,
            phase: msg.status === "completed" ? "completed" : "calculating_taxes",
            taxStages,
            currentTaxStage,
            overallProgress:
              msg.status === "completed" ? 100 : Math.min(overallProgress, 99.9),
            currentOperation: null, // no import ops during tax calc
          };
        });
        return;
      }

      // Filter by exchange
      const msgExchange =
        msg.apiKey?.exchange?.id ||
        msg.apiKey?.exchange?.name ||
        msg.exchangeId;
      if (!msgExchange) return;
      if (msgExchange.toString().toLowerCase() !== exchangeId.toString().toLowerCase()) return;

      // Dedupe
      const key = `${msg.type}-${msg.timestamp || ""}-${JSON.stringify(msg.step || "")}`;
      if (processedMsgKeys.current.has(key)) return;
      processedMsgKeys.current.add(key);

      // Keep set bounded
      if (processedMsgKeys.current.size > 200) {
        const arr = Array.from(processedMsgKeys.current);
        processedMsgKeys.current = new Set(arr.slice(-100));
      }

      lastWsEventAt.current = Date.now();

      // Per-operation tick from the importer. Carries the real operation id,
      // so it updates that row rather than being folded into a step name.
      if (msg.type === "operation_progress" && msg.operationId) {
        setProgress((prev) => {
          const ops = [...prev.operations];
          const idx = ops.findIndex((o) => o.id === msg.operationId);
          const updated: ImportOperation = {
            ...(idx >= 0
              ? ops[idx]
              : {
                  id: msg.operationId,
                  label: msg.currentStep || msg.operationId,
                  recordsPerSecond: null,
                }),
            id: msg.operationId,
            label: msg.currentStep || ops[idx]?.label || msg.operationId,
            state: msg.state === "completed" ? "completed" : msg.state === "failed" ? "failed" : "in_progress",
            recordsProcessed: msg.recordsProcessed ?? ops[idx]?.recordsProcessed ?? 0,
            estimatedTotal: msg.estimatedTotalRecords ?? ops[idx]?.estimatedTotal ?? null,
            progress: msg.progress ?? ops[idx]?.progress ?? 0,
          } as ImportOperation;

          if (idx >= 0) ops[idx] = updated;
          else ops.push(updated);

          const done = ops.filter((o) => o.state === "completed").length;
          return {
            ...prev,
            phase: "importing",
            operations: ops,
            currentOperation: ops.find((o) => o.state === "in_progress") ?? null,
            // Import owns 0-70 of the bar; tax stages own the rest.
            overallProgress: Math.min((done / Math.max(ops.length, 1)) * 70, 70),
          };
        });
        return;
      }

      // Map message to state update
      const status = msg.state || msg.status || "";
      const phase = mapPhase(status);

      // Handle completed / failed
      if (phase === "completed") {
        setProgress((prev) => ({
          ...prev,
          phase: "completed",
          overallProgress: 100,
          currentOperation: null,
          currentTaxStage: null,
          eta: null,
          operations: prev.operations.map((op) => ({
            ...op,
            state: "completed" as const,
            progress: 100,
          })),
          taxStages: prev.taxStages.map((stage) => ({
            ...stage,
            state: "completed" as const,
          })),
        }));
        return;
      }

      if (phase === "failed") {
        const errorMsg = msg.error?.message || msg.message || "Import failed";
        setProgress((prev) => ({
          ...prev,
          phase: "failed",
          currentOperation: null,
          currentTaxStage: null,
          error: {
            message: errorMsg,
            retryable: msg.error?.retryable !== false,
          },
        }));
        return;
      }

      // Progress update
      if (phase === "importing" || phase === "validating") {
        setProgress((prev) => {
          const stepData = msg.step_data || msg.step;
          const stepName = typeof stepData === "string" ? stepData : stepData?.name;
          const subProgress = typeof stepData === "object" ? stepData?.sub_progress : undefined;

          // Build updated operations
          let ops = [...prev.operations];
          if (stepName) {
            const opId = stepName.toLowerCase().replace(/\s+/g, "_");
            const existingIdx = ops.findIndex((o) => o.id === opId);

            const recordsProcessed = subProgress?.current ?? msg.recordsProcessed ?? 0;
            const estimatedTotal = subProgress?.total ?? msg.estimatedTotalRecords ?? null;

            // Compute recordsPerSecond
            if (!operationStartTimes.current.has(opId)) {
              operationStartTimes.current.set(opId, Date.now());
            }
            const elapsed = Date.now() - (operationStartTimes.current.get(opId) || Date.now());
            const rps = elapsed > 1000 && recordsProcessed > 0
              ? recordsProcessed / (elapsed / 1000)
              : null;

            const opProgress = estimatedTotal && estimatedTotal > 0
              ? Math.min((recordsProcessed / estimatedTotal) * 100, 100)
              : subProgress?.percentage ?? 0;

            const updatedOp: ImportOperation = {
              id: opId,
              label: stepName,
              state: "in_progress",
              recordsProcessed,
              estimatedTotal,
              recordsPerSecond: rps,
              durationMs: elapsed > 0 ? elapsed : null,
              progress: opProgress,
            };

            if (existingIdx >= 0) {
              // Mark earlier ops as completed
              ops = ops.map((op, i) =>
                i < existingIdx ? { ...op, state: "completed" as const, progress: 100 } : op
              );
              ops[existingIdx] = updatedOp;
            } else {
              // Mark all existing ops as completed, add new
              ops = ops.map((op) => ({ ...op, state: "completed" as const, progress: 100 }));
              ops.push(updatedOp);
            }
          }

          // Overall progress from step index
          let overall = prev.overallProgress;
          if (typeof stepData === "object" && stepData?.index != null && stepData?.max_index) {
            const sub = subProgress?.percentage ?? 0;
            overall = (stepData.index / stepData.max_index) * 100 + (sub / stepData.max_index);
          } else if (msg.progress != null) {
            overall = typeof msg.progress === "number" && msg.progress <= 1
              ? msg.progress * 100
              : msg.progress;
          }

          const currentOp = ops.find((o) => o.state === "in_progress") ?? null;
          const totalRecords = ops.reduce((sum, o) => sum + o.recordsProcessed, 0);

          return {
            ...prev,
            phase,
            operations: ops,
            currentOperation: currentOp,
            overallProgress: Math.min(overall * 0.7, 69.9), // Import phase = 0-70%
            eta: computeEta(currentOp),
            totalRecords,
            startedAt: prev.startedAt || new Date(),
          };
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, exchangeId]);

  // --- 3. Stale WS fallback polling ---
  useEffect(() => {
    if (!exchangeId) return;
    if (progress.phase === "idle" || progress.phase === "completed" || progress.phase === "failed") return;

    const interval = setInterval(async () => {
      const elapsed = Date.now() - lastWsEventAt.current;
      if (elapsed < STALE_TIMEOUT_MS && lastWsEventAt.current > 0) return;

      try {
        const token = await getAccessTokenSilently();
        const { data } = await authorizedAxios(token).get<SyncStateResponse>(
          `/sync/state/${exchangeId}`
        );

        const phase = mapPhase(data.phase);
        if (phase === "idle" && progress.phase !== "idle") {
          // Server says idle but we thought we were importing → completed
          setProgress((prev) => ({
            ...prev,
            phase: "completed",
            overallProgress: 100,
            currentOperation: null,
            currentTaxStage: null,
            eta: null,
          }));
          return;
        }

        setProgress((prev) => ({ ...prev, ...deriveFromSnapshot(data) }));
      } catch {
        // Silently fail — next poll will retry
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeId, progress.phase]);

  return progress;
};
